import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldOff, Copy, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { AuthenticatorAppLinks } from "@/components/portal/AuthenticatorAppLinks";

export const TwoFactorCard = () => {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  // setup flow: null | "password" | "qr" | "backup"
  const [setupStep, setSetupStep] = useState(null);
  const [password, setPassword] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  // disable flow
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const loadStatus = useCallback(() => {
    api.get("/auth/2fa/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(loadStatus, [loadStatus]);

  const startSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/2fa/setup", { password });
      setSetupData(res.data);
      setSetupStep("qr");
      setPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Fehler beim Starten", "Error starting setup"));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/2fa/activate", { code });
      setBackupCodes(res.data.backup_codes || []);
      setSetupStep("backup");
      setCode("");
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Ung\u00fcltiger Code", "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = () => {
    setSetupStep(null);
    setSetupData(null);
    setBackupCodes([]);
    setSavedConfirmed(false);
    toast.success(t("Zwei-Faktor-Authentifizierung aktiviert", "Two-factor authentication enabled"));
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("Kopieren fehlgeschlagen", "Copy failed"));
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/2fa/disable", { password: disablePw, code: disableCode });
      // Backend invalidiert alte Sessions und liefert ein frisches Token
      if (res.data.access_token) {
        localStorage.setItem("eleven_token", res.data.access_token);
      }
      setDisableOpen(false);
      setDisablePw("");
      setDisableCode("");
      loadStatus();
      toast.success(t("2FA deaktiviert", "2FA disabled"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Deaktivierung fehlgeschlagen", "Disable failed"));
    } finally {
      setBusy(false);
    }
  };

  const enabled = status?.enabled;

  return (
    <Card className="rounded-[16px]" data-testid="twofa-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2 flex-wrap">
          <ShieldCheck className="h-4 w-4 text-[#BFA76A] shrink-0" />
          <span>{t("Zwei-Faktor-Authentifizierung (2FA)", "Two-factor authentication (2FA)")}</span>
          {status && (
            <Badge variant={enabled ? "secondary" : "outline"} className="rounded-full text-[10px] whitespace-nowrap shrink-0" data-testid="twofa-status-badge">
              {enabled ? t("Aktiv", "Active") : t("Nicht aktiv", "Not active")}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!enabled ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground max-w-xl">
              {t(
                "Sch\u00fctzen Sie Ihr Konto mit einem zus\u00e4tzlichen Code aus einer Authenticator-App (z. B. Google oder Microsoft Authenticator). Selbst mit gestohlenem Passwort bleibt Ihr Konto gesch\u00fctzt.",
                "Protect your account with an additional code from an authenticator app (e.g. Google or Microsoft Authenticator). Even with a stolen password your account stays protected."
              )}
            </p>
            <Button onClick={() => setSetupStep("password")} className="rounded-[10px] bg-[#0B1220]" data-testid="twofa-enable-button">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> {t("2FA aktivieren", "Enable 2FA")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t("Ihr Konto ist mit 2FA gesch\u00fctzt.", "Your account is protected with 2FA.")}{" "}
              <span className="font-medium" data-testid="twofa-backup-remaining">
                {t(`${status.backup_codes_remaining} Backup-Codes \u00fcbrig.`, `${status.backup_codes_remaining} backup codes remaining.`)}
              </span>
            </p>
            <Button variant="outline" onClick={() => setDisableOpen(true)} className="rounded-[10px]" data-testid="twofa-disable-button">
              <ShieldOff className="h-4 w-4 mr-1.5" /> {t("2FA deaktivieren", "Disable 2FA")}
            </Button>
          </div>
        )}

        {/* ---- Setup-Dialog ---- */}
        <Dialog open={!!setupStep} onOpenChange={(o) => { if (!o && setupStep !== "backup") { setSetupStep(null); setSetupData(null); setPassword(""); setCode(""); } }}>
          <DialogContent className="sm:max-w-md" data-testid="twofa-setup-dialog">
            {setupStep === "password" && (
              <>
                <DialogHeader>
                  <DialogTitle>{t("2FA einrichten", "Set up 2FA")}</DialogTitle>
                  <DialogDescription>
                    {t("Sie ben\u00f6tigen eine Authenticator-App auf Ihrem Smartphone:", "You need an authenticator app on your smartphone:")}
                  </DialogDescription>
                </DialogHeader>
                <AuthenticatorAppLinks />
                <form onSubmit={startSetup} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t("Ihr Passwort zur Best\u00e4tigung", "Your password to confirm")}</Label>
                    <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-[10px]" data-testid="twofa-setup-password-input" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full rounded-[10px] bg-[#0B1220]" data-testid="twofa-setup-password-submit">
                    {t("Weiter", "Continue")}
                  </Button>
                </form>
              </>
            )}
            {setupStep === "qr" && setupData && (
              <>
                <DialogHeader>
                  <DialogTitle>{t("QR-Code scannen", "Scan QR code")}</DialogTitle>
                  <DialogDescription>
                    {t("Scannen Sie den Code mit Ihrer Authenticator-App und geben Sie dann den 6-stelligen Code ein.", "Scan the code with your authenticator app, then enter the 6-digit code.")}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4">
                  <img src={setupData.qr_data_uri} alt="2FA QR-Code" className="h-44 w-44 rounded-[12px] border border-border" data-testid="twofa-qr-image" />
                  <div className="w-full rounded-[10px] bg-muted px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("Manuelle Eingabe (falls Scan nicht m\u00f6glich)", "Manual entry (if scanning is not possible)")}</p>
                    <p className="font-mono text-xs break-all select-all" data-testid="twofa-manual-secret">{setupData.secret}</p>
                  </div>
                </div>
                <form onSubmit={activate} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t("6-stelliger Code aus der App", "6-digit code from the app")}</Label>
                    <Input
                      required inputMode="numeric" maxLength={6} placeholder="123456"
                      value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      className="rounded-[10px] text-center text-lg tracking-[0.3em]"
                      data-testid="twofa-activate-code-input"
                    />
                  </div>
                  <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-[10px] bg-[#0B1220]" data-testid="twofa-activate-submit">
                    {t("Aktivieren", "Activate")}
                  </Button>
                </form>
              </>
            )}
            {setupStep === "backup" && (
              <>
                <DialogHeader>
                  <DialogTitle>{t("Backup-Codes speichern", "Save backup codes")}</DialogTitle>
                  <DialogDescription>
                    {t("Diese Codes werden NUR JETZT angezeigt. Bewahren Sie sie sicher auf \u2013 jeder Code funktioniert genau einmal, falls Sie keinen Zugriff auf Ihre App haben.", "These codes are shown ONLY NOW. Store them safely \u2013 each code works exactly once if you lose access to your app.")}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-2" data-testid="twofa-backup-codes">
                  {backupCodes.map((c) => (
                    <div key={c} className="rounded-[8px] bg-muted px-3 py-2 font-mono text-sm text-center select-all">{c}</div>
                  ))}
                </div>
                <Button variant="outline" onClick={copyBackupCodes} className="rounded-[10px]" data-testid="twofa-copy-backup-codes">
                  {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                  {copied ? t("Kopiert", "Copied") : t("Alle Codes kopieren", "Copy all codes")}
                </Button>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox checked={savedConfirmed} onCheckedChange={(v) => setSavedConfirmed(!!v)} className="mt-0.5" data-testid="twofa-saved-checkbox" />
                  <span>{t("Ich habe die Backup-Codes sicher gespeichert.", "I have stored the backup codes safely.")}</span>
                </label>
                <Button onClick={finishSetup} disabled={!savedConfirmed} className="w-full rounded-[10px] bg-[#0B1220]" data-testid="twofa-finish-button">
                  {t("Fertig", "Done")}
                </Button>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ---- Deaktivieren-Dialog ---- */}
        <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
          <DialogContent className="sm:max-w-md" data-testid="twofa-disable-dialog">
            <DialogHeader>
              <DialogTitle>{t("2FA deaktivieren", "Disable 2FA")}</DialogTitle>
              <DialogDescription>
                {t("Aus Sicherheitsgr\u00fcnden ben\u00f6tigen wir Ihr Passwort und einen g\u00fcltigen Code (App oder Backup-Code).", "For security we need your password and a valid code (app or backup code).")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={disable} className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("Passwort", "Password")}</Label>
                <Input type="password" required value={disablePw} onChange={(e) => setDisablePw(e.target.value)} className="rounded-[10px]" data-testid="twofa-disable-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Code (App oder Backup)", "Code (app or backup)")}</Label>
                <Input required value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456 / XXXX-XXXX" className="rounded-[10px]" data-testid="twofa-disable-code-input" />
              </div>
              <Button type="submit" disabled={busy} variant="destructive" className="w-full rounded-[10px]" data-testid="twofa-disable-submit">
                {t("Endg\u00fcltig deaktivieren", "Disable permanently")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
